
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { MergedParameter } from '@/lib/ai/parameter-merge'

// ============================================================================
// CUSTOMIZE NODE POPOVER
// ============================================================================

interface CustomizeNodePopoverProps {
  settingsParams: MergedParameter[]
  pinnedParameters: string[]
  onPinnedParametersBatch: (params: string[]) => void
  nodeType?: string
}

/** Popover for bulk pin/unpin parameters and model to the node surface.
 *  Uses local state while open; flushes a single batch update on close. */
/** Group display names */
const GROUP_LABELS: Record<string, string> = {
  settings: 'Generation Settings',
  input: 'Inputs',
  output: 'Output',
}

/** Popular parameters to pin via preset, based on node type */
function getPopularParams(nodeType?: string): string[] {
  switch (nodeType) {
    case 'text':
      return ['temperature', 'max_tokens', 'top_p']
    case 'image':
      return ['guidance_scale', 'num_inference_steps', 'seed']
    case 'video':
      return ['duration', 'num_frames', 'seed']
    case '3d':
      return ['guidance_scale', 'seed']
    default:
      return ['seed']
  }
}

export function CustomizeNodePopover({
  settingsParams,
  pinnedParameters,
  onPinnedParametersBatch,
  nodeType,
}: CustomizeNodePopoverProps) {
  const [open, setOpen] = useState(false)
  const [localPinned, setLocalPinned] = useState<string[]>(pinnedParameters)
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const localPinnedRef = useRef(localPinned)
  localPinnedRef.current = localPinned

  // Sync from parent when popover opens
  useEffect(() => {
    if (open) {
      setLocalPinned(pinnedParameters)
    }
  }, [open, pinnedParameters])

  // Debounced flush — applies changes 300ms after last toggle (live preview)
  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = setTimeout(() => {
      onPinnedParametersBatch(localPinnedRef.current)
    }, 300)
  }, [onPinnedParametersBatch])

  const handleLocalToggle = useCallback((name: string) => {
    setLocalPinned(prev => {
      const next = prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
      localPinnedRef.current = next
      return next
    })
    scheduleFlush()
  }, [scheduleFlush])

  // Move pinned item up/down in localPinned array
  const handleReorder = useCallback((name: string, direction: 'up' | 'down') => {
    setLocalPinned(prev => {
      const idx = prev.indexOf(name)
      if (idx === -1) return prev
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (swapIdx < 0 || swapIdx >= prev.length) return prev
      const next = [...prev]
      next[idx] = prev[swapIdx]
      next[swapIdx] = prev[idx]
      localPinnedRef.current = next
      return next
    })
    scheduleFlush()
  }, [scheduleFlush])

  // Flush immediately on close + cleanup
  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
      const prevJoined = pinnedParameters.join(',')
      const nextJoined = localPinnedRef.current.join(',')
      if (prevJoined !== nextJoined) {
        onPinnedParametersBatch(localPinnedRef.current)
      }
    }
    setOpen(nextOpen)
  }, [pinnedParameters, onPinnedParametersBatch])

  const localModelPinned = localPinned.includes('__model__')
  const pinnedCount = localPinned.filter(n => n !== '__model__').length + (localModelPinned ? 1 : 0)

  // Pin Popular: only params that exist in settingsParams
  const popularParams = getPopularParams(nodeType)
  const availablePopular = settingsParams.filter(p => popularParams.includes(p.name))
  const showPinPopular = settingsParams.length > 0 && pinnedCount < 3 && availablePopular.length > 0

  const handlePinPopular = useCallback(() => {
    const popularNames = availablePopular.map(p => p.name)
    setLocalPinned(prev => {
      const combined = new Set(prev)
      for (const name of popularNames) {
        combined.add(name)
      }
      const next = Array.from(combined)
      localPinnedRef.current = next
      return next
    })
    scheduleFlush()
  }, [availablePopular, scheduleFlush])

  // Group parameters by their group field
  const grouped = React.useMemo(() => {
    const map = new Map<string, MergedParameter[]>()
    for (const param of settingsParams) {
      const group = param.group || 'settings'
      const list = map.get(group)
      if (list) {
        list.push(param)
      } else {
        map.set(group, [param])
      }
    }
    return map
  }, [settingsParams])

  const allSameGroup = grouped.size <= 1
  const groupEntries = Array.from(grouped.entries())

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon-sm"
                variant="ghost"
                className="relative h-6 w-6 p-0"
                aria-label="Customize node surface"
              >
                <Icon name="layout-grid" className="h-4 w-4" />
                {pinnedCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                    {pinnedCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" size="sm">
            Customize node surface
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" className="w-[280px] p-0">
        <div className="p-3 border-b border-border">
          <div className="font-mono text-[11px] font-semibold uppercase tracking-wider">Pin to Node</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Choose what appears on the node surface
          </div>
          {showPinPopular && (
            <button
              type="button"
              onClick={handlePinPopular}
              className="mt-2 inline-flex items-center gap-1.5 text-xs border border-border rounded-md px-2 py-1 hover:bg-accent transition-colors"
            >
              <Icon name="sparkles" className="h-3 w-3" />
              Pin Popular
            </button>
          )}
        </div>
        <div className="max-h-[320px] overflow-y-auto p-1.5">
          {/* Model toggle */}
          <div className="group flex items-center">
            <button
              type="button"
              onClick={() => handleLocalToggle('__model__')}
              className={cn(
                'flex-1 flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors',
                localModelPinned ? 'bg-primary/10' : 'hover:bg-accent'
              )}
            >
              <div className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                localModelPinned ? 'bg-primary border-primary' : 'border-muted-foreground/40'
              )}>
                {localModelPinned && (
                  <svg className="h-3 w-3 text-primary-foreground" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 6l3 3 5-5" />
                  </svg>
                )}
              </div>
              <Icon name="cpu" className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-sm">Model Selector</span>
            </button>
            {localModelPinned && (
              <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => handleReorder('__model__', 'up')}
                  className="h-4 w-4 flex items-center justify-center rounded hover:bg-accent"
                >
                  <Icon name="chevron-up" className="h-3 w-3 text-muted-foreground" />
                </button>
                <button
                  type="button"
                  onClick={() => handleReorder('__model__', 'down')}
                  className="h-4 w-4 flex items-center justify-center rounded hover:bg-accent"
                >
                  <Icon name="chevron-down" className="h-3 w-3 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          {settingsParams.length > 0 && (
            <div className="my-1.5 border-t border-border" />
          )}

          {/* Parameters grouped */}
          {groupEntries.map(([group, params]) => (
            <div key={group}>
              {!allSameGroup && (
                <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-medium px-2.5 pt-2 pb-1">
                  {GROUP_LABELS[group] || group.charAt(0).toUpperCase() + group.slice(1)}
                </div>
              )}
              {params.map((param) => {
                const isPinned = localPinned.includes(param.name)
                const pinnedIdx = localPinned.indexOf(param.name)
                return (
                  <div key={param.name} className="group flex items-center">
                    <button
                      type="button"
                      onClick={() => handleLocalToggle(param.name)}
                      className={cn(
                        'flex-1 flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-left transition-colors',
                        isPinned ? 'bg-primary/10' : 'hover:bg-accent'
                      )}
                    >
                      <div className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                        isPinned ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                      )}>
                        {isPinned && (
                          <svg className="h-3 w-3 text-primary-foreground" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M2 6l3 3 5-5" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm truncate">{param.label}</span>
                    </button>
                    {isPinned && (
                      <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => handleReorder(param.name, 'up')}
                          disabled={pinnedIdx === 0}
                          className="h-4 w-4 flex items-center justify-center rounded hover:bg-accent disabled:opacity-30"
                        >
                          <Icon name="chevron-up" className="h-3 w-3 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReorder(param.name, 'down')}
                          disabled={pinnedIdx === localPinned.length - 1}
                          className="h-4 w-4 flex items-center justify-center rounded hover:bg-accent disabled:opacity-30"
                        >
                          <Icon name="chevron-down" className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ))}

          {settingsParams.length === 0 && (
            <div className="px-2.5 py-4 text-center text-sm text-muted-foreground">
              Select a model to see parameters
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
